import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState, type ReactNode, type SyntheticEvent } from 'react'

import { scheduleInstalments } from '@aeris/finance'
import { formatMoney } from '@aeris/money'
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  Field,
  Input,
  MoneyInput,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeaderCell,
  TableRow,
  Textarea,
} from '@aeris/ui'

import {
  ApiError,
  createFinanceEntry,
  fetchPaymentMethods,
} from '../lib/api.ts'
import { formatDate } from '../lib/date-format.ts'
import { parseTypedMoney, toWireAmount } from '../lib/price-format.ts'

import { PartyPicker } from './party-picker.tsx'

/**
 * A new título.
 *
 * The instalment table below the form is computed by `scheduleInstalments` from
 * `@aeris/finance` — the same function the server will run. That is the point
 * of a pure domain package: somebody agreeing to "1000 em 3x" sees
 * R$ 333,34 / 333,33 / 333,33 with the dates BEFORE saving, and the numbers
 * they saw are the numbers that get written.
 */

export type FinanceEntryFormProps = {
  direction: 'receivable' | 'payable'
  onClose: () => void
}

/** `YYYY-MM-DD` for a date input, from today plus a number of days. */
const dateInputValue = (daysFromNow: number): string => {
  const target = new Date(Date.now() + daysFromNow * 86_400_000)
  const pad = (value: number): string => String(value).padStart(2, '0')
  return `${String(target.getFullYear())}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}`
}

export const FinanceEntryForm = ({
  direction,
  onClose,
}: FinanceEntryFormProps): ReactNode => {
  const queryClient = useQueryClient()

  const [partyId, setPartyId] = useState('')
  const [description, setDescription] = useState('')
  const [documentNumber, setDocumentNumber] = useState('')
  const [amount, setAmount] = useState('')
  const [instalments, setInstalments] = useState('1')
  const [firstDueAt, setFirstDueAt] = useState(() => dateInputValue(30))
  const [interval, setInterval] = useState<'monthly' | 'days'>('monthly')
  const [intervalDays, setIntervalDays] = useState('30')
  const [methodId, setMethodId] = useState('')
  const [notes, setNotes] = useState('')
  const [error, setError] = useState<string | null>(null)

  const methods = useQuery({
    queryKey: ['payment-methods'],
    queryFn: ({ signal }) => fetchPaymentMethods({ status: 'active' }, signal),
  })

  const count = Number.parseInt(instalments, 10)
  const days = Number.parseInt(intervalDays, 10)

  /**
   * The schedule, previewed with the same function the server will use.
   *
   * Recomputed on every keystroke because it is pure arithmetic over four
   * fields — there is nothing to memoise around and nothing to fetch.
   */
  const preview = useMemo(() => {
    const total = parseTypedMoney(amount)
    if (total === null || firstDueAt.length === 0) return null
    if (!Number.isInteger(count) || count < 1) return null

    return scheduleInstalments({
      total,
      count,
      // Midday rather than midnight: a date typed as a local day should not
      // become the day before in a timezone west of UTC.
      firstDueAt: new Date(`${firstDueAt}T12:00:00`),
      interval,
      ...(interval === 'days' ? { intervalDays: days } : {}),
    })
  }, [amount, count, firstDueAt, interval, days])

  const create = useMutation({
    mutationFn: async () => {
      const total = parseTypedMoney(amount)
      if (total === null) throw new Error('valor inválido')

      return await createFinanceEntry({
        direction,
        partyId,
        description: description.trim(),
        documentNumber:
          documentNumber.trim().length === 0 ? null : documentNumber.trim(),
        totalAmount: toWireAmount(total),
        instalmentCount: count,
        firstDueAt: new Date(`${firstDueAt}T12:00:00`).toISOString(),
        interval,
        ...(interval === 'days' ? { intervalDays: days } : {}),
        expectedMethodId: methodId === '' ? null : methodId,
        notes: notes.trim().length === 0 ? null : notes.trim(),
      })
    },
    onSuccess: () => {
      /*
       * Closed as soon as the server accepted, and the lists refreshed after.
       *
       * It used to await `invalidateQueries` first, which awaits the refetch of
       * every active query it touches — so the dialog stayed open, with its
       * Salvar button spinning, for as long as an unrelated list screen took to
       * reload. The person's work was finished at the 201; making them watch a
       * background refresh is the dialog reporting somebody else's latency as
       * its own.
       */
      onClose()
      void queryClient.invalidateQueries({ queryKey: ['finance'] })
    },
    onError: (cause: unknown) => {
      setError(
        cause instanceof ApiError
          ? cause.message
          : 'Não foi possível registrar o título.'
      )
    },
  })

  const submit = (event: SyntheticEvent): void => {
    event.preventDefault()
    setError(null)
    create.mutate()
  }

  const ready =
    partyId !== '' && description.trim().length > 0 && preview?.ok === true

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) onClose()
      }}
    >
      <DialogContent
        title={direction === 'receivable' ? 'Novo a receber' : 'Novo a pagar'}
        description="Um acordo entra aqui e sai como parcelas — cada uma com seu vencimento, sua baixa e seu atraso."
        className="max-w-2xl"
      >
        <form onSubmit={submit} className="flex flex-col gap-3">
          <div className="flex flex-wrap gap-3">
            <Field
              label={direction === 'receivable' ? 'Cliente' : 'Fornecedor'}
              className="min-w-64 flex-1"
            >
              {(field) => (
                <PartyPicker
                  {...field}
                  value={partyId}
                  onChange={setPartyId}
                  role={direction === 'receivable' ? 'customer' : 'supplier'}
                />
              )}
            </Field>

            <Field label="Documento" className="w-40">
              {(field) => (
                <Input
                  {...field}
                  value={documentNumber}
                  placeholder="NF, boleto…"
                  onChange={(event) => {
                    setDocumentNumber(event.target.value)
                  }}
                />
              )}
            </Field>
          </div>

          <Field label="Descrição">
            {(field) => (
              <Input
                {...field}
                value={description}
                placeholder="Aluguel 08/2026, NF 4471…"
                onChange={(event) => {
                  setDescription(event.target.value)
                }}
              />
            )}
          </Field>

          <div className="flex flex-wrap gap-3">
            <Field label="Valor total" className="w-40">
              {(field) => (
                <MoneyInput
                  {...field}
                  value={amount}
                  onValueChange={setAmount}
                />
              )}
            </Field>

            <Field label="Parcelas" className="w-28">
              {(field) => (
                <Input
                  {...field}
                  inputMode="numeric"
                  value={instalments}
                  onChange={(event) => {
                    setInstalments(event.target.value)
                  }}
                />
              )}
            </Field>

            <Field label="Primeiro vencimento" className="w-44">
              {(field) => (
                <Input
                  {...field}
                  type="date"
                  value={firstDueAt}
                  onChange={(event) => {
                    setFirstDueAt(event.target.value)
                  }}
                />
              )}
            </Field>

            <Field label="Intervalo" className="w-36">
              {(field) => (
                <Select
                  {...field}
                  value={interval}
                  onChange={(event) => {
                    setInterval(event.target.value as 'monthly' | 'days')
                  }}
                >
                  {/* Monthly keeps the day of the month — "todo dia 10" — which
                      a fixed 30-day step gets wrong four times a year. */}
                  <option value="monthly">Mensal</option>
                  <option value="days">A cada N dias</option>
                </Select>
              )}
            </Field>

            {interval === 'days' ? (
              <Field label="Dias" className="w-24">
                {(field) => (
                  <Input
                    {...field}
                    inputMode="numeric"
                    value={intervalDays}
                    onChange={(event) => {
                      setIntervalDays(event.target.value)
                    }}
                  />
                )}
              </Field>
            ) : null}
          </div>

          <div className="flex flex-wrap gap-3">
            <Field label="Forma prevista" className="w-56">
              {(field) => (
                <Select
                  {...field}
                  value={methodId}
                  onChange={(event) => {
                    setMethodId(event.target.value)
                  }}
                >
                  <option value="">Não definida</option>
                  {(methods.data?.methods ?? []).map((method) => (
                    <option key={method.id} value={method.id}>
                      {method.name}
                    </option>
                  ))}
                </Select>
              )}
            </Field>

            <Field label="Observações" className="min-w-64 flex-1">
              {(field) => (
                <Textarea
                  {...field}
                  rows={2}
                  value={notes}
                  onChange={(event) => {
                    setNotes(event.target.value)
                  }}
                />
              )}
            </Field>
          </div>

          {preview === null ? null : preview.ok ? (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeaderCell>Parcela</TableHeaderCell>
                    <TableHeaderCell>Vencimento</TableHeaderCell>
                    <TableHeaderCell className="text-right">
                      Valor
                    </TableHeaderCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {preview.instalments.map((instalment) => (
                    <TableRow key={instalment.number}>
                      <TableCell>
                        {instalment.number}/{preview.instalments.length}
                      </TableCell>
                      <TableCell>{formatDate(instalment.dueAt)}</TableCell>
                      <TableCell className="text-right font-(--font-mono)">
                        {formatMoney(instalment.amount)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <p className="text-(length:--text-xs) text-(--danger)">
              {preview.failure.reason === 'instalment-would-be-zero'
                ? 'Esse valor não se divide nesse número de parcelas sem gerar parcela de R$ 0,00.'
                : preview.failure.reason === 'count-out-of-range'
                  ? 'Número de parcelas inválido.'
                  : preview.failure.reason === 'interval-days-not-positive'
                    ? 'Informe o intervalo em dias.'
                    : 'O valor precisa ser maior que zero.'}
            </p>
          )}

          {error === null ? null : (
            <p
              role="alert"
              className="rounded-(--radius-md) border border-(--danger) bg-(--danger-subtle) px-3 py-2 text-(length:--text-xs) text-(--danger)"
            >
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!ready || create.isPending}
            >
              Registrar
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
